import { Search } from 'lucide-react'
import { SearchKeywordComboBox } from '@components/app/panels/right-panel/combo-box'
import {
  TRI_STATE_FILTER_OPTIONS,
  TrayFilterSegmentRow,
} from '@components/app/panels/right-panel/tray'
import type { ClikArtPlacedFilter } from '@/lib/clikart/clikart-placed-filter'

type ClikArtFilterControlsProps = {
  keywordNames: string[]
  selectedKeywords: string[]
  onSelectedKeywordsChange: (keywords: string[]) => void
  onKeywordOpenChange: (open: boolean) => void
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  placedFilter: ClikArtPlacedFilter
  onPlacedFilterChange: (placed: ClikArtPlacedFilter) => void
}

/** The ClikArt Filters section's expanded controls: keywords, filename search, Placed. */
export function ClikArtFilterControls({
  keywordNames,
  selectedKeywords,
  onSelectedKeywordsChange,
  onKeywordOpenChange,
  searchQuery,
  onSearchQueryChange,
  placedFilter,
  onPlacedFilterChange,
}: ClikArtFilterControlsProps) {
  return (
    // Figma node 277:23061 — "Collapse content": 12px between its groups. Its 10px top and bottom
    // padding is `TrayFiltersSection`'s own `py-2.5`, so the Filters row sits 10px + the keyword
    // group's 8px top padding (18px) from the keyword select, and the last row (Placed, which has no
    // vertical padding of its own) ends 10px above whatever follows.
    <div className="flex flex-col gap-3">
      {/* "topics": 8px above, 16px below; badges sit 8px under the select with 8px of their own
       *  vertical padding (the design's "masonry badge container"). */}
      <div className="flex flex-col pb-4 pt-2">
        <SearchKeywordComboBox
          showAddButton={false}
          placeholder={'Search by keywords'}
          keywords={keywordNames}
          value={selectedKeywords}
          onValueChange={onSelectedKeywordsChange}
          emptyMessage="No matching keywords"
          onOpenChange={onKeywordOpenChange}
          badgesClassName="py-2"
        />
      </div>

      {/* "search filenames": 8px of padding above and below the input. */}
      <div className="relative py-2">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-5 text-base-muted-foreground pointer-events-none">
          <Search className="h-4 w-4" />
        </span>
        <input
          type="text"
          placeholder="Search by filename"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          className="w-full h-9 pl-8 rounded-md border px-3 bg-background text-sm text-base-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none transition-[color,box-shadow]"
        />
      </div>

      {/* Figma node 277:23022 (RH Panel: Backgrounds, ClikArt, Templates, Spreads) — label left,
       *  joined Any/Yes/No segments right. Reuses the shared tray `TrayFilterSegmentRow` (the
       *  Photos tray's Orientation/Placed/Tagged/Favorited row geometry) rather than a new
       *  control. */}
      <TrayFilterSegmentRow
        label="Placed"
        value={placedFilter}
        options={TRI_STATE_FILTER_OPTIONS}
        onChange={onPlacedFilterChange}
        className="w-full"
        // Figma node 277:23022 (I277:23022;104:25833) — the joined Any/Yes/No control is
        // 170px wide with each segment padded px-3 py-2, not this row family's usual
        // 136px/unpadded sizing (Orientation/Tagged/Favorited in
        // photos-panel/components/photos-tray-filters.tsx).
        groupClassName="w-[170px]"
        itemClassName="px-3 py-2"
      />
    </div>
  )
}
